import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PATH } from 'src/features/navigation/utils/constants';
import { useListGatewayPricingPlans } from 'src/features/gateway/api/useListGatewayPricingPlans';
import { useListSitePricingPlans } from 'src/features/pricing/sites/api/useListSitePricingPlans';
import { usedInTypeBadgeOptions } from 'src/features/pricingPlan/utils/constants';
import { PricingPlanUsedInColumns, USED_IN_TYPE } from 'src/features/pricingPlan/utils/types';
import { useListZonePricingPlans } from 'src/features/zone/api/useListZonePricingPlans';
import NavLink from 'src/registry/components/navigation/NavLink';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

type PricingPlanUsedInTableProps = {
    pricingPlanIdentity?: string;
};

const PricingPlanUsedInTable = ({ pricingPlanIdentity }: PricingPlanUsedInTableProps) => {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const accessPricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const { ...search } = useSearch();
    const { queryFiltersOptions } = useFilters({
        initialQueryFilters: { pricing_plan_identity: pricingPlanIdentity ?? '' },
    });

    const { data: sitePricingPlan } = useListSitePricingPlans({
        parkingId,
        ...queryFiltersOptions,
        limit: 100,
        config: {
            enabled: accessPricingPlans && !!pricingPlanIdentity,
        },
    });

    const { data: zonePricingPlan } = useListZonePricingPlans({
        parkingId,
        ...queryFiltersOptions,
        limit: 100,
        config: {
            enabled: accessPricingPlans && !!pricingPlanIdentity,
        },
    });

    const { data: gatewayPricingPlan } = useListGatewayPricingPlans({
        parkingId,
        ...queryFiltersOptions,
        limit: 100,
        config: {
            enabled: accessPricingPlans && !!pricingPlanIdentity,
        },
    });

    const tableCount = (sitePricingPlan?.count ?? 0) + (zonePricingPlan?.count ?? 0) + (gatewayPricingPlan?.count ?? 0);
    const combinedResults: PricingPlanUsedInColumns[] = React.useMemo(() => {
        return [
            ...(sitePricingPlan?.results?.map((item) => ({
                ...item,
                translationName:
                    item.site_translations?.find((translation) => translation.language === locale)?.name ?? '-',
                type: USED_IN_TYPE.SITE,
            })) ?? []),
            ...(zonePricingPlan?.results?.map((item) => ({
                ...item,
                translationName:
                    item.zone_translations?.find((translation) => translation.language === locale)?.name ?? '-',
                type: USED_IN_TYPE.ZONE,
            })) ?? []),
            ...(gatewayPricingPlan?.results?.map((item) => ({
                ...item,
                translationName:
                    item.gateway_translations?.find((translation) => translation.language === locale)?.name ?? '-',
                type: USED_IN_TYPE.GATEWAY,
            })) ?? []),
        ];
    }, [sitePricingPlan?.results, zonePricingPlan?.results, gatewayPricingPlan?.results, locale]);

    const usedInTableData = {
        count: tableCount ?? 0,
        offset: 0,
        limit: 0,
        results: combinedResults,
    };

    const columnHelper = createColumnHelper<PricingPlanUsedInColumns>();

    const columns = [
        columnHelper.accessor('translationName', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const rowType = row.original.type;

                const name = row.original.translationName;

                const urlToRouteTo =
                    rowType === USED_IN_TYPE.SITE
                        ? `site/${row.original.site}`
                        : rowType === USED_IN_TYPE.ZONE
                          ? `zone/${row.original.zone}`
                          : `gateway/${row.original.gateway}`;
                return name !== '-' ? (
                    <NavLink routerClassName={() => ''} to={`/${PATH.ADMINISTRATION}/${urlToRouteTo}`}>
                        <div className="text-primary-700 flex cursor-pointer flex-row items-center gap-1 font-medium">
                            {row.original.translationName}
                            <FontAwesomeIcon icon={ICON_MAP.ARROW_UP} />
                        </div>
                    </NavLink>
                ) : (
                    '-'
                );
            },
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: usedInTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(commonMessages.activeFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(commonMessages.activeTo),
            },
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={usedInTableData}
            enableSearchParams={false}
            search={search}
            semantics={{
                title: formatMessage(pricingPlanMessages.usedIn),
                entryName: formatMessage(pricingPlanMessages.plan).toLowerCase(),
                entryNames: formatMessage(pricingPlanMessages.plansUsedInTable).toLowerCase(),
                placeholder: formatMessage(pricingPlanMessages.plan).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default PricingPlanUsedInTable;
